// A curated set of major airports with live ATC feeds.
//
// `atc` lists LiveATC.net feed "mounts" (channels/frequencies) for that field —
// Tower, Ground, Approach, etc. The client plays them through /api/atc/:mount
// and lets you switch between them. Every mount below was verified live against
// LiveATC; where a field has no feed (regulatory reasons are common in Europe
// and Australia) `atc` is empty and the panel offers a "Browse feeds on LiveATC"
// link instead — so every airport icon is still useful.
//
// Feed mounts occasionally change on LiveATC; add or correct them here.

export const AIRPORTS = [
  // ── North America ────────────────────────────────────────────────
  { icao: "KJFK", iata: "JFK", name: "New York — John F. Kennedy", lat: 40.6413, lon: -73.7781,
    atc: [
      { label: "Tower", mount: "kjfk_twr" },
      { label: "Ground", mount: "kjfk_gnd" },
      { label: "Departure", mount: "kjfk_dep" },
      { label: "ATIS", mount: "kjfk_atis" },
    ] },
  { icao: "KLAX", iata: "LAX", name: "Los Angeles Intl", lat: 33.9416, lon: -118.4085,
    atc: [{ label: "Tower", mount: "klax_twr" }, { label: "Ground", mount: "klax_gnd" }] },
  { icao: "KSFO", iata: "SFO", name: "San Francisco Intl", lat: 37.6213, lon: -122.379,
    atc: [
      { label: "Tower", mount: "ksfo_twr" },
      { label: "Ground", mount: "ksfo_gnd" },
      { label: "Departure", mount: "ksfo_dep" },
      { label: "ATIS", mount: "ksfo_atis" },
    ] },
  { icao: "KORD", iata: "ORD", name: "Chicago — O'Hare", lat: 41.9742, lon: -87.9073,
    atc: [{ label: "Tower", mount: "kord_twr" }, { label: "Approach", mount: "kord_app" }] },
  { icao: "KATL", iata: "ATL", name: "Atlanta — Hartsfield-Jackson", lat: 33.6407, lon: -84.4277,
    atc: [{ label: "Tower", mount: "katl_twr" }] },
  { icao: "KBOS", iata: "BOS", name: "Boston — Logan", lat: 42.3656, lon: -71.0096,
    atc: [
      { label: "Tower", mount: "kbos_twr" },
      { label: "Ground", mount: "kbos_gnd" },
      { label: "Departure", mount: "kbos_dep" },
    ] },
  { icao: "KSEA", iata: "SEA", name: "Seattle — Tacoma", lat: 47.4502, lon: -122.3088,
    atc: [{ label: "Tower", mount: "ksea_twr" }, { label: "Approach", mount: "ksea_app" }] },
  { icao: "KMIA", iata: "MIA", name: "Miami Intl", lat: 25.7959, lon: -80.287,
    atc: [{ label: "Tower", mount: "kmia_twr" }, { label: "Approach", mount: "kmia_app" }] },
  { icao: "KLAS", iata: "LAS", name: "Las Vegas — Harry Reid", lat: 36.084, lon: -115.1537, atc: [] },
  { icao: "KDFW", iata: "DFW", name: "Dallas–Fort Worth", lat: 32.8998, lon: -97.0403, atc: [] },
  { icao: "CYYZ", iata: "YYZ", name: "Toronto — Pearson", lat: 43.6777, lon: -79.6248,
    atc: [{ label: "Tower", mount: "cyyz_twr" }, { label: "Approach", mount: "cyyz_app" }] },

  // ── Europe (LiveATC coverage is sparse here for legal reasons) ────
  { icao: "EHAM", iata: "AMS", name: "Amsterdam — Schiphol", lat: 52.3105, lon: 4.7683,
    atc: [{ label: "Tower", mount: "eham_twr" }] },
  { icao: "EGLL", iata: "LHR", name: "London — Heathrow", lat: 51.47, lon: -0.4543, atc: [] },
  { icao: "EGKK", iata: "LGW", name: "London — Gatwick", lat: 51.1537, lon: -0.1821, atc: [] },
  { icao: "EDDF", iata: "FRA", name: "Frankfurt am Main", lat: 50.0379, lon: 8.5622, atc: [] },
  { icao: "LFPG", iata: "CDG", name: "Paris — Charles de Gaulle", lat: 49.0097, lon: 2.5479, atc: [] },
  { icao: "EIDW", iata: "DUB", name: "Dublin", lat: 53.4213, lon: -6.2701, atc: [] },
  { icao: "LEMD", iata: "MAD", name: "Madrid — Barajas", lat: 40.4936, lon: -3.5668, atc: [] },

  // ── Asia / Pacific / Middle East ────────────────────────────────
  { icao: "RJTT", iata: "HND", name: "Tokyo — Haneda", lat: 35.5494, lon: 139.7798,
    atc: [{ label: "Tower", mount: "rjtt_twr" }, { label: "ATIS", mount: "rjtt_atis" }] },
  { icao: "VHHH", iata: "HKG", name: "Hong Kong Intl", lat: 22.308, lon: 113.9185,
    atc: [{ label: "Tower", mount: "vhhh_twr" }, { label: "ATIS", mount: "vhhh_atis" }] },
  { icao: "OMDB", iata: "DXB", name: "Dubai Intl", lat: 25.2532, lon: 55.3657,
    atc: [{ label: "Tower", mount: "omdb_twr" }] },
  { icao: "YSSY", iata: "SYD", name: "Sydney — Kingsford Smith", lat: -33.9399, lon: 151.1753,
    atc: [
      { label: "Tower", mount: "yssy_twr" },
      { label: "Ground", mount: "yssy_gnd" },
      { label: "Approach", mount: "yssy_app" },
    ] },
  { icao: "YBBN", iata: "BNE", name: "Brisbane", lat: -27.3842, lon: 153.1175,
    atc: [{ label: "Tower", mount: "ybbn_twr" }] },
  { icao: "YMML", iata: "MEL", name: "Melbourne — Tullamarine", lat: -37.6733, lon: 144.8433, atc: [] },
  { icao: "YSCB", iata: "CBR", name: "Canberra", lat: -35.3069, lon: 149.195, atc: [] },
];

// LiveATC's own listen page for an airport — used as a fallback so users can
// always find a working feed even if our mounts are stale.
export const liveatcUrl = (icao) =>
  `https://www.liveatc.net/search/?icao=${encodeURIComponent(String(icao || "").toLowerCase())}`;
